import { Hospital, Info } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DISTANCE_OPTIONS } from "@/constants";
import type { RootState } from "@/store/store";
import type Distance from "@/model/distance";
import { setDistance } from "@/store/provider-search/provider-search-slice";
import { NoDataAvailable } from "@/pages/components/empty";

export default function NoProvidersFound() {
  const dispatch = useDispatch();
  const { distance } = useSelector((state: RootState) => state.providerSearch);

  const fetchNextPossibleDistance = () => {
    const nextPossibleDistance = DISTANCE_OPTIONS.find(
      (item, _) => item.value === distance.value,
    );
    const index = DISTANCE_OPTIONS.indexOf(nextPossibleDistance as Distance);
    const nextIndex = index + 1;
    if (nextIndex < DISTANCE_OPTIONS.length) {
      return DISTANCE_OPTIONS[nextIndex];
    }
    return null;
  };

  const updateDistance = () => {
    const distanceToSet = fetchNextPossibleDistance() as Distance;
    dispatch(setDistance(distanceToSet));
  };

  return (
    <div className="p-4">
      {!fetchNextPossibleDistance() ? (
        <NoDataAvailable
          message="Sorry, we couldn't find any providers"
          icon={Hospital}
        />
      ) : (
        <Card className="mx-auto w-full max-w-md bg-white shadow-md md:max-w-full">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              {/* Info Icon */}
              <div className="mt-0.5 flex-shrink-0">
                <div className="flex size-6 items-center justify-center rounded-full bg-blue-100">
                  <Info className="size-4 text-blue-600" />
                </div>
              </div>
              {/* Message Content */}
              <div className="flex-1">
                <p className="mb-6 text-sm leading-relaxed text-gray-700">
                  Sorry, we couldn&apos;t find this provider. It&apos;s either
                  not covered by your insurance or there are no providers in
                  your area who offer it.
                </p>
                {fetchNextPossibleDistance() && (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-800">
                      Please try the following option:
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-blue-500 bg-transparent text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                      onClick={updateDistance}
                    >
                      {`Try changing the distance: ${(fetchNextPossibleDistance() as Distance).displayName}`}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
